import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { daysSince } from '../../utils/Utils.js';
import AnimalNotes from '../../shared/AnimalNotes.js';
import Calves from '../Calves.js';
import EditableDamSireField from '../EditableDamSireField.js';
import './HerdCardList.css';
import '../HerdExpandedComponent.css';

const sexSymbol = (sex) => {
  if (sex === 'm') return 'M';
  if (sex === 'f') return 'F';
  return sex || '—';
};

const formatAge = (date) => {
  if (!date) return '—';
  const months = Math.floor(daysSince(date) / 30);
  if (months < 1) return '< 1m';
  return `${months}m`;
};

const formatDate = (date) => {
  if (!date) return '—';
  return date.substring(0, 10);
};

const HerdCard = ({ row, onRefresh }) => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const sexClass = row.sex === 'm' ? 'male' : row.sex === 'f' ? 'female' : '';

  return (
    <div className={`herd-card ${expanded ? 'expanded' : ''}`} onClick={() => setExpanded(e => !e)}>
      <div className="herd-card-main">
        <div className="herd-card-tag">{t('tag_prefix')}-{row.tag}</div>
        <div className="herd-card-meta">
          <span className="herd-card-breed">{row.breed || '—'}</span>
          <span className={`herd-card-sex ${sexClass}`}>{sexSymbol(row.sex)}</span>
        </div>
        <div className="herd-card-secondary">
          <span>{row.area || t('unknown')}</span>
          <span>{formatAge(row.birth_date)}</span>
        </div>
        <div className="herd-card-badges">
          {row.is_alive === false && (
            <span className="herd-card-badge dead">{t('dead')}</span>
          )}
          {row.is_pregnant && (
            <span className="herd-card-badge pregnant">{t('pregnant')}</span>
          )}
        </div>
        <div className="herd-card-chevron">{expanded ? '▲' : '▼'}</div>
      </div>

      {expanded && (
        <div className="herd-card-detail" onClick={e => e.stopPropagation()}>
          <div className="herd-card-detail-row">
            <span>{t('dam_mother')}</span>
            <EditableDamSireField tag={row.tag} field="dam" value={row.dam} onSaved={onRefresh} />
          </div>
          <div className="herd-card-detail-row">
            <span>{t('sire_father')}</span>
            <EditableDamSireField tag={row.tag} field="father" value={row.father} onSaved={onRefresh} />
          </div>
          <div className="herd-card-detail-row">
            <span>{t('birth_date')}</span><span>{formatDate(row.birth_date)}</span>
          </div>
          <div className="herd-card-detail-row">
            <span>{t('status')}</span><span>{row.is_alive === false ? t('dead') : t('alive')}</span>
          </div>
          {row.is_alive === false && (
            <div className="herd-card-detail-row">
              <span>{t('death_cause')}</span><span>{row.death_cause || '—'}</span>
            </div>
          )}
          <div className="herd-card-detail-row">
            <span>{t('registered_by')}</span><span>{row.created_by || '—'}</span>
          </div>
          <div className="herd-card-detail-row">
            <span>{t('registered_from')}</span><span>{row.phone || '—'}</span>
          </div>

          <AnimalNotes data={row} onNoteAdded={onRefresh} />
          <Calves tag={row.tag} field={row.sex === 'm' ? 'father' : 'dam'} />
        </div>
      )}
    </div>
  );
};

const HerdCardList = ({ data, onRefresh }) => {
  return (
    <div className="herd-card-list">
      {data && data.map(row => (
        <HerdCard key={row._id} row={row} onRefresh={onRefresh} />
      ))}
      {(!data || data.length === 0) && (
        <div className="herd-card-empty">—</div>
      )}
    </div>
  );
};

export default HerdCardList;
